/** Management View analytics — result shape. JSON-serialisable so a backend can return it unchanged. */
import type { Member, MetricKind, Project, ProjectStatus, ProjectUpdate, WorkLog } from '@/types/models';

export type ManagementPeriod = 'today' | 'last7' | 'last30' | 'month' | 'prevmonth' | 'quarter' | 'custom' | 'all';
export const MANAGEMENT_PERIODS: [ManagementPeriod, string][] = [
  ['today', 'Today'],
  ['last7', 'Last 7 days'],
  ['last30', 'Last 30 days'],
  ['month', 'This month'],
  ['prevmonth', 'Previous month'],
  ['quarter', 'This quarter'],
  ['custom', 'Custom range'],
  ['all', 'All time'],
];

/** Filters of Management View. Empty arrays mean "all". */
export interface ManagementFilter {
  period: ManagementPeriod;
  from: string;
  to: string;
  /** project ids; `__general` = General (no project) work */
  projects: string[];
  members: string[];
  statuses: ProjectStatus[];
  /** lowercase trimmed lead names; `__none` = no lead assigned */
  leads: string[];
}

export type HealthLevel = 'critical' | 'attention' | 'healthy' | 'done';
export type UpdateRisk = 'high' | 'medium' | 'low' | 'na';
export type DeadlineBucket = 'overdue' | 'due7' | 'due30' | 'later' | 'completed' | 'none';

export interface ProjectRow {
  id: string;
  project: Project;
  name: string;
  service: string;
  lead: string;
  status: ProjectStatus;
  /** Live, In Progress or Planning */
  active: boolean;
  /** Hours logged in the period (member filter applies) */
  hours: number;
  entries: number;
  /** Latest Project Log date, all time ('' = never) */
  lastUpdate: string;
  /** Days since the latest Project Log (null = never) */
  sinceUpdate: number | null;
  /** Active and no Project Log for 2+ days, or none */
  stale: boolean;
  /** Current deadline ('' = none) */
  deadline: string;
  /** Days until the current deadline (negative = overdue; null = none) */
  daysLeft: number | null;
  /** Number of deadline changes (all time) */
  extensionCount: number;
  /** Days added by all deadline changes */
  daysAdded: number;
  latestReason: string;
  /** Project Logs dated in the period */
  updatesInPeriod: number;
  /** Days in the period with a work-log entry or a Project Log */
  activeDays: number;
  bucket: DeadlineBucket;
  overdue: boolean;
  onTime: boolean;
  health: { level: HealthLevel; reasons: string[] };
  risk: UpdateRisk;
}

export interface Aggregate {
  hours: number;
  entries: number;
  days: string[];
}
export interface ProjectAggregate extends Aggregate {
  /** member keys (member id or `__unknown`) */
  members: string[];
}
export interface MemberAggregate extends Aggregate {
  /** project keys (project id, `__general` or `__unknown`) */
  projects: string[];
}

export interface MetricPoint {
  date: string;
  value: number;
  unit: string;
}
export interface MetricSeries {
  key: string;
  projectId: string;
  label: string;
  /** '' = nobody set the type: recorded values only, no total, no change */
  kind: MetricKind | '';
  points: MetricPoint[];
  /** Non-numeric values found (not charted) */
  textValues: number;
  first: MetricPoint | null;
  last: MetricPoint | null;
  unit: string;
  percentage: boolean;
  /** "That day only" and not a percentage → values are added up */
  summed: boolean;
  total: number;
  /** last − first, when the type is set and there are 2+ values */
  change: number | null;
}

export interface ExtensionRow {
  projectId: string;
  projectName: string;
  on: string;
  from: string;
  to: string;
  reason: string;
  days: number;
}

export interface ManagementAnalytics {
  filter: ManagementFilter;
  today: string;
  /** [from, to]; '' = open */
  range: [string, string];
  /** days in the period (null for All time / open ranges) */
  days: number | null;
  /** same number of days immediately before the period */
  previousRange: [string, string] | null;
  allProjects: Project[];
  allMembers: Member[];
  projectFilterOn: boolean;
  memberFilterOn: boolean;
  rows: ProjectRow[];
  team: Member[];
  /** work logs in scope and in the period */
  logs: WorkLog[];
  /** work logs in scope, all dates */
  scopeLogs: WorkLog[];
  /** Project Logs in scope and in the period (one per project per day) */
  updates: ProjectUpdate[];
  byProject: Record<string, ProjectAggregate>;
  byMember: Record<string, MemberAggregate>;
  /** hours by `${memberKey}\u0001${projectKey}` */
  matrix: Record<string, number>;
  /** latest work date per member key (all dates, in scope) */
  lastWorked: Record<string, string>;
  /** existing members with more than 0 hours in the period */
  activeMemberIds: string[];
  hours: number;
  /** hours logged by active members */
  activeHours: number;
  previous: { hours: number; activeMembers: number; projectsUpdated: number };
  metrics: MetricSeries[];
  extensions: ExtensionRow[];
  extensionsInPeriod: ExtensionRow[];
  dataQuality: string[];
}

/** One project's slice of the Management View (Project analysis section; AnalyticsService.getProjectAnalytics). */
export interface ProjectAnalysis {
  row: ProjectRow | null;
  metrics: MetricSeries[];
  extensions: ExtensionRow[];
}

export const GENERAL = '__general';
export const UNKNOWN = '__unknown';
